import { EditorView } from "@codemirror/view";
import { unfoldEffect } from "@codemirror/language";
import type { IntelligenceCodeAction, SourceRange } from "@magic-md/engine-client/protocol";
import { useT } from "@magic-md/i18n/react";
import { Edit3, ListTree, Search, Wand2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCodeMirror } from "../hooks/useCodeMirror";
import { setIntelligencePreviewRange } from "../lib/codemirror-intelligence";
import type { IntelligenceRenameRequest } from "../lib/codemirror-intelligence/actions";
import { sourceRangeToOffsets } from "../lib/codemirror-intelligence/helpers";
import { isCompressedExternalJsonPath } from "../lib/external-output-path";
import { isBinaryFile } from "../lib/file-type";
import { createSerializerTargetLocatorFromLine } from "../lib/unit-edit-target";
import {
    findFoldRangeStartingOnLine,
    setEngineDefinitions,
    setExecutionDurations,
    setExecutionErrors,
    setExecutionStatuses,
    setCloudWorkerPluginNames,
    setForceL2Mode,
    setForExecutionDurations,
    setForExecutionStatuses,
    setInlineRenderedDefinitions,
    setParseDiagnostics,
    setRefResolveContext,
    setWatchedUnitIds,
} from "../lib/codemirror-magic-syntax";
import { usePluginRegistryVersion } from "../hooks/usePluginUI";
import {
    createWorkspacePinnedUnit,
    findWorkspacePinnedUnit,
} from "../lib/pinned-units";
import { findParentContentBlockId } from "../lib/scope-variables";
import { pluginUIRegistry } from "../plugins/plugin-ui-registry";
import { useEditorStore } from "../store/editor-store";
import { useBookmarkStore } from "../store/bookmark-store";
import { useHistoryStore } from "../store/history-store";
import { useLogStore } from "../store/log-store";
import { buildExtensionContextMenuItems } from "../extensions/extension-menu-bridge";
import {
    collectWatchedUnitIdsForDocument,
    useOutputWatchStore,
} from "../store/output-watch-store";
import {
    clearRunningStartTime,
    getRunningStartTime,
    MINIMUM_RUNNING_MS,
} from "../store/execution-state-transitions";
import {
    type ExecutionStatus,
    type ForExecutionStatus,
    usePluginExecutionStore,
} from "../store/plugin-execution-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";
import { frontendLog } from "../utils/frontend-logger";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { DefinitionPopover, type PopoverTarget } from "./DefinitionPopover";
import { MetaConfigPopover, type MetaPopoverTarget } from "./MetaConfigPopover";
import { TemplateInputsPopover, type TemplateInputsTarget } from "./TemplateInputsPopover";
import { usePaneEditorContext } from "./PaneContext";

type IntelligenceMenuState =
    | {
        kind: "context";
        x: number;
        y: number;
        items: ContextMenuItem[];
    }
    | {
        kind: "references";
        x: number;
        y: number;
        items: Array<{
            label: string;
            line: number;
            column: number;
            endLine: number;
            endColumn: number;
        }>;
    }
    | {
        kind: "symbols";
        x: number;
        y: number;
        items: Array<{
            label: string;
            detail?: string;
            line: number;
            column: number;
        }>;
    }
    | {
        kind: "code-actions";
        x: number;
        y: number;
        actions: IntelligenceCodeAction[];
    };

const CIRCULAR_DEPENDENCY_RE = /\u5faa\u73af\u4f9d\u8d56|circular/i;

function resolveSyntaxEditPosition(
    view: EditorView,
    target: HTMLElement,
): number | null {
    try {
        const pos = view.posAtDOM(target, 0);
        if (typeof pos === "number") return pos;
    } catch {
        // Fall through to coordinate-based resolution.
    }

    const rect = target.getBoundingClientRect();
    return (
        view.posAtCoords({
            x: rect.left + 6,
            y: rect.top + rect.height / 2,
        }) ?? null
    );
}

function resolveSyntaxTargetLine(
    view: EditorView,
    target: HTMLElement,
): number | null {
    const position = resolveSyntaxEditPosition(view, target);
    if (position == null) return null;
    return view.state.doc.lineAt(position).number - 1;
}

export function EditorPanel() {
    const t = useT("editor");
    const paneCtx = usePaneEditorContext();
    const {
        tabId: activeTabId,
        tab: activeTab,
        executions,
        definitions,
        definitionOutputs,
        forDefinitions,
        parsedDocument,
        forExecutions,
        contentExecutions,
        importExecutions,
        useExecutions,
        contentDefinitions,
    } = paneCtx;
    const activeContent = activeTab?.content ?? "";
    const isLoading = !!activeTab?.isLoading;
    const isReadOnlyFile =
        isBinaryFile(activeTab?.filePath) ||
        isCompressedExternalJsonPath(activeTab?.filePath);
    const setContent = useEditorStore((s) => s.setContent);
    const codeMirrorWidgetMode = useEditorStore((s) => s.codeMirrorWidgetMode);
    const pluginRegistryRevision = usePluginRegistryVersion();
    const inlineRenderedDefinitionIds = useEditorStore(
        (s) => s.inlineRenderedDefinitionIds,
    );
    const outputWatches = useOutputWatchStore((state) => state.watches);
    const watchedUnitSignature = useMemo(() => {
        const ids = collectWatchedUnitIdsForDocument(outputWatches, {
            documentPath: activeTab?.filePath ?? null,
            sessionId: paneCtx.sessionId,
        });
        return [...ids].sort().join("\u0000");
    }, [activeTab?.filePath, outputWatches, paneCtx.sessionId]);

    const [popoverTarget, setPopoverTarget] = useState<PopoverTarget | null>(
        null,
    );
    const [metaPopoverTarget, setMetaPopoverTarget] =
        useState<MetaPopoverTarget | null>(null);
    const [templateInputsTarget, setTemplateInputsTarget] =
        useState<TemplateInputsTarget | null>(null);
    const [intelligenceMenu, setIntelligenceMenu] =
        useState<IntelligenceMenuState | null>(null);
    const [renameRequest, setRenameRequest] =
        useState<IntelligenceRenameRequest | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const renameInputRef = useRef<HTMLInputElement>(null);

    const [bookmarkMenu, setBookmarkMenu] = useState<{
        x: number;
        y: number;
        path: string;
        line: number;
        existingId: string | null;
        existingLabel: string | null;
    } | null>(null);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                x: number;
                y: number;
                path: string;
                line: number;
                existingId: string | null;
                existingLabel: string | null;
            };
            setBookmarkMenu(detail);
        };
        window.addEventListener("magic-md:bookmark-menu", handler);
        return () => window.removeEventListener("magic-md:bookmark-menu", handler);
    }, []);

    const bookmarkMenuItems: ContextMenuItem[] = bookmarkMenu
        ? bookmarkMenu.existingId
            ? [
                  {
                      key: "rename",
                      label: t("editor.bookmark.rename"),
                      onClick: () => {
                          const next = window.prompt(
                              t("editor.bookmark.labelPrompt"),
                              bookmarkMenu.existingLabel ?? "",
                          );
                          if (next === null) return;
                          void useBookmarkStore.getState().update(bookmarkMenu.existingId!, {
                              label: next.trim() || null,
                          });
                      },
                  },
                  {
                      key: "remove",
                      label: t("editor.bookmark.delete"),
                      onClick: () => {
                          void useBookmarkStore.getState().remove(bookmarkMenu.existingId!);
                      },
                  },
              ]
            : [
                  {
                      key: "add",
                      label: t("editor.bookmark.addAtLine", { line: bookmarkMenu.line }),
                      onClick: () => {
                          void useBookmarkStore.getState().add({
                              path: bookmarkMenu.path,
                              line: bookmarkMenu.line,
                          });
                      },
                  },
                  {
                      key: "add-with-label",
                      label: t("editor.bookmark.addWithLabel"),
                      onClick: () => {
                          const label = window.prompt(t("editor.bookmark.labelPrompt"));
                          if (label === null) return;
                          void useBookmarkStore.getState().add({
                              path: bookmarkMenu.path,
                              line: bookmarkMenu.line,
                              label: label.trim() || undefined,
                          });
                      },
                  },
              ]
        : [];

    // Track content that came from CodeMirror's own onChange so we can
    // distinguish it from external changes (e.g. PluginPanel config edits).
    const cmContentRef = useRef(activeContent);
    const definitionsRef = useRef(definitions);
    const forDefinitionsRef = useRef(forDefinitions);
    const contentDefinitionsRef = useRef(contentDefinitions);
    const parsedDocumentRef = useRef(parsedDocument);
    const useExecutionsRef = useRef(useExecutions);
    const activeTabIdRef = useRef(activeTabId);
    const activeFilePathRef = useRef(activeTab?.filePath);

    useEffect(() => {
        definitionsRef.current = definitions;
    }, [definitions]);

    useEffect(() => {
        activeTabIdRef.current = activeTabId;
        activeFilePathRef.current = activeTab?.filePath;
    }, [activeTab?.filePath, activeTabId]);

    useEffect(() => {
        forDefinitionsRef.current = forDefinitions;
    }, [forDefinitions]);

    useEffect(() => {
        contentDefinitionsRef.current = contentDefinitions;
    }, [contentDefinitions]);

    useEffect(() => {
        parsedDocumentRef.current = parsedDocument;
    }, [parsedDocument]);

    useEffect(() => {
        useExecutionsRef.current = useExecutions;
    }, [useExecutions]);

    const onChange = useCallback(
        (content: string) => {
            cmContentRef.current = content;
            setContent(content, activeTabId);
        },
        [setContent, activeTabId],
    );

    const { mountRef, replaceContent, viewRef } = useCodeMirror({
        initialContent: activeContent,
        onChange,
        paneId: paneCtx.paneId,
        readOnly: isReadOnlyFile,
    });

    const revealLocation = useCallback(
        (lineNumber: number, column = 0) => {
            const view = viewRef.current;
            if (!view) return;
            const safeLineNumber = Math.max(
                1,
                Math.min(lineNumber, view.state.doc.lines),
            );
            const line = view.state.doc.line(safeLineNumber);
            const pos = Math.min(line.from + column, line.to);
            view.dispatch({
                selection: { anchor: pos },
                effects: EditorView.scrollIntoView(pos, { y: "center" }),
            });
            view.focus();
        },
        [viewRef],
    );

    const previewLocation = useCallback(
        (range: SourceRange | null) => {
            const view = viewRef.current;
            if (!view) return;
            if (!range) {
                view.dispatch({
                    effects: setIntelligencePreviewRange.of(null),
                });
                return;
            }
            const safeLineNumber = Math.max(
                1,
                Math.min(range.startLine, view.state.doc.lines),
            );
            const line = view.state.doc.line(safeLineNumber);
            const pos = Math.min(line.from + range.startColumn, line.to);
            view.dispatch({
                effects: [
                    setIntelligencePreviewRange.of(range),
                    EditorView.scrollIntoView(pos, { y: "nearest" }),
                ],
            });
        },
        [viewRef],
    );

    const closeIntelligenceMenu = useCallback(() => {
        previewLocation(null);
        setIntelligenceMenu(null);
    }, [previewLocation]);

    const closeRenameRequest = useCallback(() => {
        setRenameRequest(null);
        setRenameValue("");
    }, []);

    // Push execution statuses and error messages from Zustand store into CodeMirror state

    // Tick counter bumped by a timer when a minimum-running-display window expires,
    // forcing the useEffect below to re-evaluate and transition to the real status.
    const [displayTick, setDisplayTick] = useState(0);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;

        const now = Date.now();
        let soonestExpiry = Infinity;
        // displayTick is incremented by the timer below to force re-evaluation
        // when a minimum-running window expires.
        void displayTick;

        /** Return effective status: keeps "running" visible for MINIMUM_RUNNING_MS */
        const applyMinRunning = (
            id: string,
            actual: ExecutionStatus,
        ): ExecutionStatus => {
            if (actual === "running") return "running";
            const t = getRunningStartTime(id);
            if (t !== undefined) {
                const expiry = t + MINIMUM_RUNNING_MS;
                if (now < expiry) {
                    if (expiry < soonestExpiry) soonestExpiry = expiry;
                    return "running";
                }
                clearRunningStartTime(id);
            }
            return actual;
        };
        const applyMinRunningFor = (
            id: string,
            actual: ForExecutionStatus,
        ): ForExecutionStatus => {
            if (actual === "running") return "running";
            const t = getRunningStartTime(id);
            if (t !== undefined) {
                const expiry = t + MINIMUM_RUNNING_MS;
                if (now < expiry) {
                    if (expiry < soonestExpiry) soonestExpiry = expiry;
                    return "running";
                }
                clearRunningStartTime(id);
            }
            return actual;
        };

        const statusMap = new Map<string, ExecutionStatus>();
        const errorMap = new Map<string, string>();
        const durationMap = new Map<string, number | null>();
        for (const [id, entry] of executions) {
            const effective = applyMinRunning(id, entry.status);
            statusMap.set(id, effective);
            durationMap.set(
                id,
                effective === "running" && entry.status !== "running"
                    ? null
                    : entry.duration,
            );
            // Collect error messages for inline display
            if (
                entry.status === "error" &&
                entry.result &&
                !entry.result.success &&
                entry.result.message
            ) {
                errorMap.set(id, entry.result.message);
            }
        }
        // Content execution status + errors
        for (const [id, entry] of contentExecutions) {
            statusMap.set(id, applyMinRunning(id, entry.status));
            if (entry.status === "error" && entry.error) {
                errorMap.set(id, entry.error);
            }
        }
        // Import execution status + errors
        for (const [id, entry] of importExecutions) {
            const effective = applyMinRunning(id, entry.status);
            statusMap.set(id, effective);
            if (entry.status === "error" && entry.error) {
                errorMap.set(id, entry.error);
            }
            // Imported public symbols (ns/id) should mirror their parent import
            // unit status so passive dependency-triggered imports are visible on
            // the symbols users actually reference.
            if (entry.symbols) {
                for (const symbol of entry.symbols) {
                    if (!statusMap.has(symbol.qualifiedId)) {
                        statusMap.set(symbol.qualifiedId, effective);
                    }
                    if (
                        entry.status === "error" &&
                        entry.error &&
                        !errorMap.has(symbol.qualifiedId)
                    ) {
                        errorMap.set(symbol.qualifiedId, entry.error);
                    }
                }
            }
        }
        // Use execution status + errors
        for (const [id, entry] of useExecutions) {
            statusMap.set(id, applyMinRunning(id, entry.status));
            if (entry.status === "error" && entry.error) {
                errorMap.set(id, entry.error);
            }
        }
        // Content durations (hide when minimum-running override is active)
        for (const [id, entry] of contentExecutions) {
            const eff = statusMap.get(id);
            durationMap.set(
                id,
                eff === "running" && entry.status !== "running" ? null : entry.duration,
            );
        }
        // Import durations
        for (const [id, entry] of importExecutions) {
            const eff = statusMap.get(id);
            const dur =
                eff === "running" && entry.status !== "running" ? null : entry.duration;
            durationMap.set(id, dur);
            if (entry.symbols) {
                for (const symbol of entry.symbols) {
                    if (!durationMap.has(symbol.qualifiedId)) {
                        durationMap.set(symbol.qualifiedId, dur);
                    }
                }
            }
        }
        // Use durations
        for (const [id, entry] of useExecutions) {
            const eff = statusMap.get(id);
            durationMap.set(
                id,
                eff === "running" && entry.status !== "running" ? null : entry.duration,
            );
        }
        // Build for-execution status map
        const forStatusMap = new Map<string, ForExecutionStatus>();
        const forDurationMap = new Map<string, number | null>();
        for (const [id, entry] of forExecutions) {
            const eff = applyMinRunningFor(id, entry.status);
            forStatusMap.set(id, eff);
            forDurationMap.set(
                id,
                eff === "running" && entry.status !== "running"
                    ? null
                    : (entry.duration ?? null),
            );
        }
        view.dispatch({
            effects: [
                setExecutionStatuses.of(statusMap),
                setExecutionErrors.of(errorMap),
                setExecutionDurations.of(durationMap),
                setForExecutionStatuses.of(forStatusMap),
                setForExecutionDurations.of(forDurationMap),
                setRefResolveContext.of({
                    documentPath: activeTab?.filePath ?? null,
                    parsedDoc: parsedDocument,
                    executions,
                    forExecutions,
                    contentExecutions,
                    importExecutions,
                    engineDefinitions: definitions,
                    engineContentDefinitions: contentDefinitions,
                    getOutput: (id) =>
                        executions.get(id)?.output ?? definitionOutputs.get(id) ?? null,
                }),
            ],
        });

        // Schedule re-render when the soonest minimum-running window expires
        if (soonestExpiry < Infinity) {
            const delay = Math.max(16, soonestExpiry - now);
            const timer = setTimeout(() => setDisplayTick((t) => t + 1), delay);
            return () => clearTimeout(timer);
        }
    }, [
        executions,
        forExecutions,
        contentExecutions,
        importExecutions,
        useExecutions,
        parsedDocument,
        definitions,
        definitionOutputs,
        contentDefinitions,
        viewRef,
        displayTick,
    ]);

    // Push parse diagnostics (e.g. nested ::for errors) into CodeMirror
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            effects: [setParseDiagnostics.of(parsedDocument?.diagnostics ?? [])],
        });
    }, [parsedDocument?.diagnostics, viewRef]);

    // Push pane-scoped definitions (includes imported ns/id) into CM
    // so that build-decorations can resolve use block source plugin names.
    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        const engineDefMap = new Map<string, string>();
        for (const [id, def] of definitions) {
            engineDefMap.set(id, def.pluginName);
        }
        view.dispatch({
            effects: [setEngineDefinitions.of(engineDefMap)],
        });
    }, [definitions, viewRef]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        const names = new Set<string>();
        for (const [name, plugin] of pluginUIRegistry.getAll()) {
            if (plugin.source?.kind === "cloud-worker") names.add(name);
        }
        view.dispatch({
            effects: [setCloudWorkerPluginNames.of(names)],
        });
    }, [pluginRegistryRevision, viewRef]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            effects: [
                setInlineRenderedDefinitions.of(new Set(inlineRenderedDefinitionIds)),
            ],
        });
    }, [inlineRenderedDefinitionIds, viewRef]);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            effects: [
                setWatchedUnitIds.of(
                    new Set(
                        watchedUnitSignature
                            ? watchedUnitSignature.split("\u0000")
                            : [],
                    ),
                ),
            ],
        });
    }, [viewRef, watchedUnitSignature]);

    // Keep CodeMirror content in sync with the current tab and external edits,
    // but perform at most one replacement per render pass. This avoids a
    // tab switch briefly scheduling both the generic content-sync path and the
    // tab-switch path under rapid close-tab shortcut sequences.
    const prevTabIdRef = useRef(activeTabId);
    useEffect(() => {
        if (prevTabIdRef.current !== activeTabId) {
            prevTabIdRef.current = activeTabId;
            cmContentRef.current = activeContent;
            replaceContent(activeContent, {
                preserveSelection: false,
            });
            return;
        }

        if (activeContent !== cmContentRef.current) {
            cmContentRef.current = activeContent;
            replaceContent(activeContent);
        }
    }, [activeTabId, activeContent, replaceContent]);

    // Auto-focus the editor when a document is opened or a tab is switched
    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            viewRef.current?.focus();
        });
        return () => cancelAnimationFrame(frame);
    }, [activeTabId, viewRef]);

    // Listen for "goto-line" event (e.g. from TOC panel)
    useEffect(() => {
        let flashTimer: ReturnType<typeof setTimeout> | null = null;

        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                line: number;
                flash?: boolean;
            };
            if (!detail?.line) return;
            const view = viewRef.current;
            if (!view) return;
            const lineNo = Math.min(detail.line, view.state.doc.lines);
            const line = view.state.doc.line(lineNo);
            view.dispatch({
                selection: { anchor: line.from },
                effects: EditorView.scrollIntoView(line.from, { y: "center" }),
            });
            view.focus();

            // Subtle flash highlight on the target line
            // Delay slightly so the scroll settles first (matches preview behaviour)
            if (detail.flash) {
                if (flashTimer) clearTimeout(flashTimer);
                flashTimer = setTimeout(() => {
                    requestAnimationFrame(() => {
                        const coords = view.coordsAtPos(line.from);
                        if (!coords) return;
                        const elements = view.contentDOM.querySelectorAll(".cm-line");
                        let targetLine: HTMLElement | null = null;
                        for (const el of elements) {
                            const rect = el.getBoundingClientRect();
                            if (rect.top <= coords.top + 2 && rect.bottom >= coords.top - 2) {
                                targetLine = el as HTMLElement;
                                break;
                            }
                        }
                        if (!targetLine) return;
                        targetLine.classList.remove("toc-flash");
                        void targetLine.offsetWidth;
                        targetLine.classList.add("toc-flash");
                        flashTimer = setTimeout(() => {
                            targetLine?.classList.remove("toc-flash");
                        }, 2000);
                    });
                }, 150);
            }
        };

        window.addEventListener("magic-md:goto-line", handler);
        return () => {
            window.removeEventListener("magic-md:goto-line", handler);
            if (flashTimer) clearTimeout(flashTimer);
        };
    }, [viewRef]);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                target?: HTMLElement;
                position?: number;
            };
            if (!(detail?.target instanceof HTMLElement)) return;

            const view = viewRef.current;
            if (!view) return;

            const pos = typeof detail.position === "number"
                ? detail.position
                : resolveSyntaxEditPosition(view, detail.target);
            if (pos == null) return;

            const foldRange = findFoldRangeStartingOnLine(view.state, pos);
            const effects = foldRange
                ? [
                    unfoldEffect.of(foldRange),
                    EditorView.scrollIntoView(pos, { y: "center" }),
                ]
                : EditorView.scrollIntoView(pos, { y: "center" });

            view.focus();
            view.dispatch({
                selection: { anchor: pos },
                effects,
            });
        };

        window.addEventListener("magic-md:enter-syntax-edit", handler);
        return () =>
            window.removeEventListener("magic-md:enter-syntax-edit", handler);
    }, [viewRef]);

    // Listen for the "select definition" event dispatched by the CodeMirror config button
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                definitionId: string;
                x: number;
                y: number;
                sourceElement?: HTMLElement;
                unitKind?: "define" | "use";
            };
            if (!detail?.definitionId) return;

            const sourceElement = detail.sourceElement;
            const view = viewRef.current;
            if (sourceElement && view && !view.dom.contains(sourceElement)) return;
            const sourceLine = sourceElement && view
                ? resolveSyntaxTargetLine(view, sourceElement)
                : null;
            const locator = sourceLine == null || !detail.unitKind
                ? undefined
                : createSerializerTargetLocatorFromLine({
                        content: view?.state.doc.toString() ?? "",
                        unitKind: detail.unitKind,
                        unitId: detail.definitionId,
                        line: sourceLine,
                    });
            if (sourceLine != null && detail.unitKind && !locator) return;

            const editorState = useEditorStore.getState();

            if (editorState.rightSidebarOpen) {
                // Route to right sidebar instead of popover
                editorState.setRightSidebarDefinitionId(
                    detail.definitionId,
                    locator,
                    locator ? {
                        tabId: activeTabId,
                        paneId: paneCtx.paneId,
                        filePath: activeTab?.filePath,
                        unitKind: detail.unitKind,
                    } : undefined,
                );
            } else {
                setPopoverTarget({
                    definitionId: detail.definitionId,
                    x: detail.x,
                    y: detail.y,
                    locator,
                    unitKind: detail.unitKind,
                    tabId: activeTabId,
                    paneId: paneCtx.paneId,
                    filePath: activeTab?.filePath,
                });
            }
        };

        window.addEventListener("magic-md:select-definition", handler);
        return () =>
            window.removeEventListener("magic-md:select-definition", handler);
    }, [activeTab?.filePath, activeTabId, paneCtx.paneId]);

    // Listen for the "select meta" event dispatched by the CodeMirror meta config button
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                domain: string;
                x: number;
                y: number;
                paneId?: string;
                tabId?: string;
            };
            if (!detail?.domain) return;
            if (detail.paneId && detail.paneId !== paneCtx.paneId) return;
            if (!detail.paneId && detail.tabId && detail.tabId !== activeTabId) return;
            const tabId = detail.tabId ?? activeTabId;
            if (!tabId) return;
            if (detail.paneId) {
                useEditorStore.getState().setFocusedPane(detail.paneId);
            }
            setMetaPopoverTarget({
                domain: detail.domain,
                x: detail.x,
                y: detail.y,
                tabId,
                paneId: detail.paneId ?? paneCtx.paneId,
            });
        };

        window.addEventListener("magic-md:select-meta", handler);
        return () => window.removeEventListener("magic-md:select-meta", handler);
    }, [activeTabId, paneCtx.paneId]);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                unitKind: TemplateInputsTarget["unitKind"];
                unitId: string;
                x: number;
                y: number;
                sourceElement?: HTMLElement;
            };
            if (!detail?.unitId || !detail.unitKind || !activeTabId) return;
            if (
                detail.sourceElement &&
                viewRef.current &&
                !viewRef.current.dom.contains(detail.sourceElement)
            ) {
                return;
            }
            const sourceLine = detail.sourceElement && viewRef.current
                ? resolveSyntaxTargetLine(viewRef.current, detail.sourceElement)
                : null;
            const locator = sourceLine == null || !viewRef.current
                ? undefined
                : createSerializerTargetLocatorFromLine({
                    content: viewRef.current.state.doc.toString(),
                    unitKind: detail.unitKind,
                    unitId: detail.unitId,
                    line: sourceLine,
                });
            if (sourceLine != null && !locator) return;
            setTemplateInputsTarget({
                unitKind: detail.unitKind,
                unitId: detail.unitId,
                x: detail.x,
                y: detail.y,
                tabId: activeTabId,
                paneId: paneCtx.paneId,
                filePath: activeTab?.filePath,
                ...(locator ? { locator } : {}),
            });
        };

        window.addEventListener("magic-md:edit-template-inputs", handler);
        return () => window.removeEventListener("magic-md:edit-template-inputs", handler);
    }, [activeTab?.filePath, activeTabId, paneCtx.paneId]);

    // Listen for the "run definition" event dispatched by the CodeMirror run button
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                definitionId: string;
            };
            if (!detail?.definitionId) return;
            const executionState = usePluginExecutionStore.getState();
            const currentDefinitions = definitionsRef.current;
            const currentContentDefinitions = contentDefinitionsRef.current;
            const def = currentDefinitions.get(detail.definitionId);
            if (def) {
                executionState.runPrimary(def, currentDefinitions).catch((err) => {
                    frontendLog.error("[EditorPanel] runPrimary failed", err, {
                        source: "ui",
                    });
                });
            } else {
                // Check if it's an inner define inside a content block — run parent content
                const parentContentId = findParentContentBlockId(
                    detail.definitionId,
                    currentContentDefinitions,
                );
                if (parentContentId) {
                    executionState.runContentAction(parentContentId).catch((err) => {
                        frontendLog.error(
                            "[EditorPanel] runContentAction (from inner define) failed",
                            err,
                            {
                                source: "ui",
                            },
                        );
                    });
                }
            }
        };

        const cancelHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                definitionId: string;
            };
            if (!detail?.definitionId) return;
            usePluginExecutionStore.getState().cancelExecution(detail.definitionId);
        };

        const pinHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                definitionId: string;
            };
            if (!detail?.definitionId) return;
            // Only handle if this pane owns the definition (prevents multi-pane double-toggle)
            const definition = definitionsRef.current.get(detail.definitionId);
            const documentPath = activeFilePathRef.current;
            const tabId = activeTabIdRef.current;
            if (!definition || !documentPath || !tabId) return;
            const editor = useEditorStore.getState();
            const existing = findWorkspacePinnedUnit(
                editor.pinnedUnits,
                documentPath,
                detail.definitionId,
            );
            if (existing) {
                editor.removePinnedUnit(existing.id);
                return;
            }
            usePluginExecutionStore
                .getState()
                ._ensureSession(tabId)
                .then((sessionId) => {
                    useEditorStore.getState().addPinnedUnit(
                        createWorkspacePinnedUnit(documentPath, definition),
                        { sessionId },
                    );
                })
                .catch((err) => {
                    frontendLog.error("[EditorPanel] pin session failed", err, {
                        source: "ui",
                    });
                });
        };

        const inlineRenderHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                definitionId: string;
                sourceElement?: HTMLElement;
            };
            if (!detail?.definitionId) return;
            const sourceElement = detail.sourceElement;
            if (
                sourceElement &&
                viewRef.current &&
                !viewRef.current.dom.contains(sourceElement)
            ) {
                return;
            }
            const id = detail.definitionId;
            if (!sourceElement) {
                const ownsInlineRenderableUnit =
                    definitionsRef.current.has(id) ||
                    forDefinitionsRef.current.has(id) ||
                    contentDefinitionsRef.current.has(id) ||
                    useExecutionsRef.current.has(id) ||
                    (parsedDocumentRef.current?.uses ?? []).some((use) => use.id === id);
                if (!ownsInlineRenderableUnit) return;
            }
            useEditorStore
                .getState()
                .toggleInlineRenderedDefinition(id);
        };

        window.addEventListener("magic-md:run-definition", handler);
        window.addEventListener("magic-md:cancel-definition", cancelHandler);
        window.addEventListener("magic-md:pin-definition", pinHandler);
        window.addEventListener("magic-md:toggle-inline-render", inlineRenderHandler);
        return () => {
            window.removeEventListener("magic-md:run-definition", handler);
            window.removeEventListener("magic-md:cancel-definition", cancelHandler);
            window.removeEventListener("magic-md:pin-definition", pinHandler);
            window.removeEventListener(
                "magic-md:toggle-inline-render",
                inlineRenderHandler,
            );
        };
    }, []);

    // Listen for the "run for" event dispatched by the CodeMirror for-block run button
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { forId: string };
            if (!detail?.forId) return;
            usePluginExecutionStore
                .getState()
                .runForAction(detail.forId)
                .catch((err) => {
                    frontendLog.error("[EditorPanel] runForAction failed", err, {
                        source: "ui",
                    });
                });
        };

        window.addEventListener("magic-md:run-for", handler);
        return () => window.removeEventListener("magic-md:run-for", handler);
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { forId: string };
            if (!detail?.forId) return;
            usePluginExecutionStore.getState().cancelExecution(detail.forId);
        };

        window.addEventListener("magic-md:cancel-for", handler);
        return () => window.removeEventListener("magic-md:cancel-for", handler);
    }, []);

    // Listen for "run content" event dispatched by ContentButtonGroupWidget

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { definitionId: string };
            if (!detail?.definitionId) return;
            usePluginExecutionStore
                .getState()
                .runContentAction(detail.definitionId)
                .catch((err) => {
                    frontendLog.error("[EditorPanel] runContentAction failed", err, {
                        source: "ui",
                    });
                });
        };

        window.addEventListener("magic-md:run-content", handler);
        return () => window.removeEventListener("magic-md:run-content", handler);
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { unitId: string };
            if (!detail?.unitId) return;
            usePluginExecutionStore.getState().clearExecution(detail.unitId);
        };

        window.addEventListener("magic-md:clear-execution", handler);
        return () => window.removeEventListener("magic-md:clear-execution", handler);
    }, []);

    // Listen for "run import" event dispatched by ImportButtonGroupWidget
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                namespace: string;
                source: string;
            };
            if (!detail?.namespace) return;
            // Import's unitId = namespace
            usePluginExecutionStore
                .getState()
                .runImportAction(detail.namespace)
                .catch((err) => {
                    frontendLog.error("[EditorPanel] runImportAction failed", err, {
                        source: "ui",
                    });
                });
        };

        const cancelHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { namespace: string };
            if (!detail?.namespace) return;
            usePluginExecutionStore.getState().cancelExecution(detail.namespace);
        };

        window.addEventListener("magic-md:run-import", handler);
        window.addEventListener("magic-md:cancel-import", cancelHandler);
        return () => {
            window.removeEventListener("magic-md:run-import", handler);
            window.removeEventListener("magic-md:cancel-import", cancelHandler);
        };
    }, []);

    // Listen for "run use" event dispatched by UseButtonGroupWidget
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { useId: string };
            if (!detail?.useId) return;
            usePluginExecutionStore
                .getState()
                .runUseAction(detail.useId)
                .catch((err) => {
                    frontendLog.error("[EditorPanel] runUseAction failed", err, {
                        source: "ui",
                    });
                });
        };

        window.addEventListener("magic-md:run-use", handler);
        return () => window.removeEventListener("magic-md:run-use", handler);
    }, []);

    // Listen for "cancel use" event dispatched by UseButtonGroupWidget
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { useId: string };
            if (!detail?.useId) return;
            usePluginExecutionStore.getState().cancelExecution(detail.useId);
        };

        window.addEventListener("magic-md:cancel-use", handler);
        return () => window.removeEventListener("magic-md:cancel-use", handler);
    }, []);

    useEffect(() => {
        const view = viewRef.current;
        if (!view) return;
        view.dispatch({
            effects: setForceL2Mode.of(codeMirrorWidgetMode === "raw"),
        });
    }, [codeMirrorWidgetMode, viewRef]);

    // Listen for "open debug panel" event dispatched by ButtonGroupWidget
    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as { definitionId: string };
            if (!detail?.definitionId) return;
            const sessionId = usePluginExecutionStore.getState().sessionId;
            if (!sessionId) return;
            useLogStore
                .getState()
                .openDebugPanel({ sessionId, targetId: detail.definitionId });
        };
        window.addEventListener("magic-md:open-debug-panel", handler);
        return () =>
            window.removeEventListener("magic-md:open-debug-panel", handler);
    }, []);

    // Snapshot trigger from CM editor button widgets.
    useEffect(() => {
        const filePath = activeTab?.filePath;
        if (!filePath) return;
        const handler = (e: Event) => {
            const detail = (e as CustomEvent).detail as
                | { trigger?: "manual" }
                | undefined;
            const snapshotInput: { filePath: string; trigger?: "manual" | "on-save" | "auto-pre-restore" } = {
                filePath,
                trigger: detail?.trigger ?? "manual",
            };
            (async () => {
                try {
                    const setStatusMessage = useEditorStore.getState().setStatusMessage;
                    setStatusMessage(t("editor.snapshot.inProgress"), undefined);
                    const result = await useHistoryStore.getState().take(snapshotInput);
                    if (result) {
                        setStatusMessage(t("editor.snapshot.succeeded"), 4000);
                    } else {
                        setStatusMessage(t("editor.snapshot.failed"), 4000);
                    }
                } catch (error) {
                    const setStatusMessage = useEditorStore.getState().setStatusMessage;
                    setStatusMessage(
                        t("editor.snapshot.error", {
                            message: error instanceof Error ? error.message : t("common.unknown"),
                        }),
                        4000,
                    );
                }
            })();
        };
        window.addEventListener("magic-md:snapshot-document", handler);
        return () => {
            window.removeEventListener("magic-md:snapshot-document", handler);
        };
    }, [activeTab?.filePath, t]);

    useEffect(() => {
        const renameHandler = (e: Event) => {
            const detail = (e as CustomEvent<IntelligenceRenameRequest>).detail;
            if (!detail?.definitionId) return;
            setRenameRequest(detail);
            setRenameValue(detail.initialValue);
        };

        const contextMenuHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                x: number;
                y: number;
                items: Array<{
                    key: string;
                    label: string;
                    shortcut?: string;
                    disabled?: boolean;
                    separator?: boolean;
                    icon: "edit" | "search" | "wand";
                    runAfterClose?: boolean;
                    onSelect: () => void;
                }>;
                selection?: {
                    text: string;
                    range: { from: number; to: number };
                };
            };
            const builtInItems: ContextMenuItem[] = (detail?.items ?? []).map((item) => ({
                key: item.key,
                label: item.label,
                shortcut: item.shortcut,
                disabled: item.disabled,
                separator: item.separator,
                icon:
                    item.icon === "edit"
                        ? <Edit3 size={14} />
                        : item.icon === "search"
                            ? <Search size={14} />
                            : <Wand2 size={14} />,
                onClick: () => {
                    if (item.runAfterClose) {
                        closeIntelligenceMenu();
                        requestAnimationFrame(() => {
                            item.onSelect();
                        });
                        return;
                    }
                    item.onSelect();
                },
            }));
            const extensionItems = detail?.selection
                ? buildExtensionContextMenuItems(
                    {
                        surface: "editor-selection",
                        selection: detail.selection,
                        path: activeTab?.filePath,
                        tabId: activeTabId,
                    },
                    { firstSeparator: builtInItems.length > 0 },
                )
                : [];
            const items = [...builtInItems, ...extensionItems];
            if (items.length === 0) return;
            setIntelligenceMenu({
                kind: "context",
                x: detail.x,
                y: detail.y,
                items,
            });
        };

        const referencesHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                x: number;
                y: number;
                items: Array<{
                    label: string;
                    line: number;
                    column: number;
                    endLine: number;
                    endColumn: number;
                }>;
            };
            if (!detail?.items?.length) return;
            setIntelligenceMenu({
                kind: "references",
                x: detail.x,
                y: detail.y,
                items: detail.items,
            });
        };

        const symbolsHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                x: number;
                y: number;
                items: Array<{
                    label: string;
                    detail?: string;
                    line: number;
                    column: number;
                }>;
            };
            if (!detail?.items?.length) return;
            setIntelligenceMenu({
                kind: "symbols",
                x: detail.x,
                y: detail.y,
                items: detail.items,
            });
        };

        const codeActionsHandler = (e: Event) => {
            const detail = (e as CustomEvent).detail as {
                x: number;
                y: number;
                actions: IntelligenceCodeAction[];
            };
            if (!detail?.actions?.length) return;
            setIntelligenceMenu({
                kind: "code-actions",
                x: detail.x,
                y: detail.y,
                actions: detail.actions,
            });
        };

        window.addEventListener(
            "magic-md:intelligence-open-rename",
            renameHandler,
        );
        window.addEventListener(
            "magic-md:intelligence-show-context-menu",
            contextMenuHandler,
        );
        window.addEventListener(
            "magic-md:intelligence-show-references",
            referencesHandler,
        );
        window.addEventListener(
            "magic-md:intelligence-show-symbols",
            symbolsHandler,
        );
        window.addEventListener(
            "magic-md:intelligence-show-code-actions",
            codeActionsHandler,
        );

        return () => {
            window.removeEventListener(
                "magic-md:intelligence-open-rename",
                renameHandler,
            );
            window.removeEventListener(
                "magic-md:intelligence-show-context-menu",
                contextMenuHandler,
            );
            window.removeEventListener(
                "magic-md:intelligence-show-references",
                referencesHandler,
            );
            window.removeEventListener(
                "magic-md:intelligence-show-symbols",
                symbolsHandler,
            );
            window.removeEventListener(
                "magic-md:intelligence-show-code-actions",
                codeActionsHandler,
            );
        };
    }, [activeTab?.filePath, activeTabId, closeIntelligenceMenu]);

    useEffect(() => {
        if (!renameRequest) return;
        requestAnimationFrame(() => {
            renameInputRef.current?.focus();
            renameInputRef.current?.select();
        });
    }, [renameRequest]);

    useEffect(() => {
        if (!renameRequest) return;

        const handlePointer = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) return;
            if (renameInputRef.current?.closest(".magic-rename-popover")?.contains(target)) {
                return;
            }
            closeRenameRequest();
        };

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                closeRenameRequest();
            }
        };

        document.addEventListener("mousedown", handlePointer, true);
        document.addEventListener("keydown", handleKey, true);
        return () => {
            document.removeEventListener("mousedown", handlePointer, true);
            document.removeEventListener("keydown", handleKey, true);
        };
    }, [renameRequest, closeRenameRequest]);

    const closePopover = useCallback(() => setPopoverTarget(null), []);
    const closeMetaPopover = useCallback(() => setMetaPopoverTarget(null), []);
    const closeTemplateInputs = useCallback(() => setTemplateInputsTarget(null), []);

    const executionError = usePluginExecutionStore((s) => s.executionError);


    const intelligenceMenuItems: ContextMenuItem[] = intelligenceMenu
        ? intelligenceMenu.kind === "context"
            ? intelligenceMenu.items
            : intelligenceMenu.kind === "references"
                ? intelligenceMenu.items.map((item, index) => ({
                    key: `ref-${index}-${item.line}-${item.column}`,
                    label: item.label,
                    icon: <Search size={14} />,
                    shortcut: `L${item.line}`,
                    onHover: () => {
                        previewLocation({
                            startLine: item.line,
                            startColumn: item.column,
                            endLine: item.endLine,
                            endColumn: item.endColumn,
                        });
                    },
                    onHoverEnd: () => previewLocation(null),
                    onClick: () => {
                        revealLocation(item.line, item.column);
                        closeIntelligenceMenu();
                    },
                }))
                : intelligenceMenu.kind === "symbols"
                    ? intelligenceMenu.items.map((item, index) => ({
                        key: `symbol-${index}-${item.line}-${item.column}`,
                        label: item.label,
                        icon: <ListTree size={14} />,
                        shortcut: item.detail,
                        onClick: () => {
                            revealLocation(item.line, item.column);
                            closeIntelligenceMenu();
                        },
                    }))
                    : intelligenceMenu.actions.map((action, index) => ({
                        key: `action-${index}`,
                        label: action.title,
                        icon: <Wand2 size={14} />,
                        shortcut: action.isPreferred ? t("editor.codeAction.preferred") : undefined,
                        disabled: !action.edit,
                        onClick: () => {
                            const view = viewRef.current;
                            if (!view || !action.edit) return;
                            const offsets = sourceRangeToOffsets(
                                view.state.doc,
                                action.edit.range,
                            );
                            if (!offsets) return;
                            view.dispatch({
                                changes: {
                                    from: offsets.from,
                                    to: offsets.to,
                                    insert: action.edit.newText,
                                },
                            });
                            closeIntelligenceMenu();
                        },
                    }))
        : [];

    return (
        <div className="flex flex-col h-full min-w-0">
            {executionError && CIRCULAR_DEPENDENCY_RE.test(executionError) && (
                <div
                    className="border-b px-4 py-3 relative shrink-0 theme-accent-banner"
                    style={getThemeAccentStyle("error")}
                    role="alert"
                >
                    <strong className="font-bold">{t("editor.circularFailedTitle")}</strong>
                    <span className="block sm:inline"> {executionError}</span>
                    <p className="text-sm mt-1">
                        {t("editor.circularFailedHint")}
                    </p>
                </div>
            )}

            {/* CodeMirror container */}
            <div className="relative flex-1 min-h-0 overflow-hidden">
                <div ref={mountRef} className="absolute inset-0" />
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                        <div className="flex flex-col items-center gap-2">
                            <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                            <span className="text-xs text-muted-foreground">Loading…</span>
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Definition popover (floats above editor) */}
            <DefinitionPopover target={popoverTarget} onClose={closePopover} />

            {/* Meta config popover (floats above editor) */}
            <MetaConfigPopover
                target={metaPopoverTarget}
                onClose={closeMetaPopover}
            />

            <TemplateInputsPopover
                target={templateInputsTarget}
                onClose={closeTemplateInputs}
            />

            {renameRequest ? (
                <div
                    className="magic-rename-popover fixed z-[10000] w-[260px] rounded-lg border border-border bg-popover p-2 shadow-xl"
                    style={{ left: renameRequest.x, top: renameRequest.y }}
                >
                    <form
                        className="space-y-2"
                        onSubmit={(event) => {
                            event.preventDefault();
                            const nextName = renameValue.trim();
                            if (!nextName) return;
                            renameRequest.onSubmit(nextName);
                            closeRenameRequest();
                        }}
                    >
                        <div className="text-[11px] font-medium text-foreground">
                            {t("editor.renameTitle", { id: renameRequest.definitionId })}
                        </div>
                        <input
                            ref={renameInputRef}
                            value={renameValue}
                            onChange={(event) => setRenameValue(event.target.value)}
                            className="h-8 w-full rounded border border-border bg-background px-2 text-xs outline-none focus:border-primary"
                        />
                        <div className="flex items-center justify-end gap-2">
                            <button
                                type="button"
                                className="rounded border border-border px-2 py-1 text-[11px] text-muted-foreground hover:text-foreground"
                                onClick={closeRenameRequest}
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                type="submit"
                                className="rounded bg-primary px-2 py-1 text-[11px] text-primary-foreground"
                            >
                                {t("dialog.confirm")}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <ContextMenu
                open={!!intelligenceMenu}
                x={intelligenceMenu?.x ?? 0}
                y={intelligenceMenu?.y ?? 0}
                items={intelligenceMenuItems}
                onClose={closeIntelligenceMenu}
            />

            <ContextMenu
                open={!!bookmarkMenu}
                x={bookmarkMenu?.x ?? 0}
                y={bookmarkMenu?.y ?? 0}
                items={bookmarkMenuItems}
                onClose={() => setBookmarkMenu(null)}
            />
        </div>
    );
}
